import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard, StatusPill } from "./shared";
import { fundTypeLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

export default function FundViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/society/accounting/engine/funds/${id}`);
        setFund(res.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load this fund"));
        navigate(`${ACCOUNTING_BASE}/funds`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!fund) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/funds`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Funds
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">{fund.fundName}</h2>
        </div>
        <div className="flex gap-2">
          <Link to={`${ACCOUNTING_BASE}/reports/fund-position`}><Button variant="outline">View Fund Position</Button></Link>
          <Button onClick={() => navigate(`${ACCOUNTING_BASE}/funds/${id}/edit`)} className="bg-[#111827] text-white hover:bg-[#374151]">
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        </div>
      </div>

      <SectionCard title="Fund details">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Fund code" value={fund.fundCode} />
          <DetailField label="Fund type" value={fundTypeLabels[fund.fundType] || fund.fundType} />
          <DetailField label="Linked ledger" value={fund.linkedLedgerName} />
          <DetailField label="Start date" value={fund.startDate} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
            <div className="mt-1"><StatusPill active={fund.active !== false} /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Ledger history</div>
            <div className="mt-1"><StatusPill active={!fund.hasPostedTransactions} trueLabel="No posted vouchers yet" falseLabel="Has posted vouchers" /></div>
          </div>
        </div>
        {fund.description && <div className="mt-4"><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Description</div><p className="mt-1 text-sm text-[#374151]">{fund.description}</p></div>}
        {fund.notes && <div className="mt-4"><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Notes</div><p className="mt-1 text-sm text-[#374151]">{fund.notes}</p></div>}
      </SectionCard>

      <SectionCard title="Record history">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Created" value={fund.createdAt ? new Date(fund.createdAt).toLocaleString() : "-"} />
          <DetailField label="Created by" value={fund.createdBy} />
          <DetailField label="Last updated" value={fund.updatedAt ? new Date(fund.updatedAt).toLocaleString() : "-"} />
          <DetailField label="Updated by" value={fund.updatedBy || fund.createdBy} />
        </div>
      </SectionCard>
    </div>
  );
}
